import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FilebrokennotfoundIllustration } from "vega-illustrations";

import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { HorizontalStepIndicator } from "@vega-core/components/Navigation/Steppers/HorizontalStepIndicator";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { useTasksStateContext } from "@vega-modules/context/TasksStateContext/context";
import { CommonPaths } from "@vega-modules/paths";

import { TASK_RENDERERS } from "./stepsV2/taskRenderers";
import { WelcomeHeader } from "./WelcomeHeader";

export const WelcomeTasksPageV2 = () => {
  const navigate = useNavigate();
  const tasksState = useTasksStateContext();

  if (tasksState.version !== "v2") {
    throw new Error("WelcomeTasksPageV2 requires the v2 tasks state");
  }

  const {
    pendingTasks,
    hasPendingTasks,
    isLoading,
    isCompleted,
    isFetched,
    isError,
    refetch,
  } = tasksState;

  const [activeStep, setActiveStep] = useState(0);
  const isMobile = !useDeviceWidth("sm");

  const steps = pendingTasks.map((task) => task.taskType);

  const handleNextStep = () => {
    if (activeStep < pendingTasks.length - 1) {
      setActiveStep((step) => step + 1);

      return;
    }

    refetch();
  };

  useEffect(
    function redirectOnCompletion() {
      if (isFetched && isCompleted) {
        navigate(CommonPaths.Home);
      }
    },
    [isFetched, isCompleted, navigate],
  );

  const currentTask = pendingTasks.at(activeStep);
  const Renderer = currentTask
    ? TASK_RENDERERS[currentTask.taskType]
    : undefined;

  useEffect(
    function skipUnsupportedTask() {
      if (currentTask && !Renderer) {
        handleNextStep();
      }
    },
    [currentTask, Renderer, handleNextStep],
  );

  const getStepComponent = () => {
    if (isLoading) return <LoadingSpinner />;

    if (!currentTask || !Renderer) return null;

    return <Renderer onComplete={handleNextStep} task={currentTask} />;
  };

  if (isError && !hasPendingTasks) {
    return (
      <div className="flex flex-col min-h-dvh bg-background/secondary text-text/primary">
        <WelcomeHeader />
        <main className="flex-1 flex flex-col gap-4 items-center justify-center">
          <FilebrokennotfoundIllustration />
          <ActionStatusMessage
            contactLabel={(m.titles_contactUs() as string).toLowerCase()}
            message={m.errors_unexpectedErrorChunk1()}
            type={NoticeType.ERROR}
            withContact
          />
        </main>
      </div>
    );
  }

  return (
    <div
      className={cnTwMerge(
        "flex flex-col min-h-dvh bg-background/secondary text-text/primary",
        isMobile && "h-dvh overflow-hidden",
      )}
    >
      <WelcomeHeader />

      <main
        className={cnTwMerge(
          "flex-1 flex items-center justify-center text-center",
          isMobile && "min-h-0 overflow-y-auto items-start",
        )}
      >
        {getStepComponent()}
      </main>

      <footer
        className={cnTwMerge(
          "h-13 flex items-center justify-center mt-14",
          isMobile && "mt-0",
        )}
      >
        <div className="w-[480px]">
          {steps.length > 1 ? (
            <HorizontalStepIndicator activeStep={activeStep} steps={steps} />
          ) : null}
        </div>
      </footer>
    </div>
  );
};
